import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Field from '../../../common/components/field';
import FarmSelect from '../../../common/components/farm-select';
import RaceSelect from '../../../common/components/race-select';
import { IconClose } from '../../../common/icons';
import { useClientFarms } from '../../../common/contexts/farms';
import { envelopeData } from '../../../core/network/envelope';
import {
    SUBMISSION_APPEARANCE_MAX,
    SUBMISSION_NAME_MAX,
    SUBMISSION_NUMBER_MAX,
} from '../../../core/constants/forms';
import { submissionKeys } from '../queries/keys';
import { useCreateSubmission, useUpdateSubmission } from '../contexts/submissions';

import '../styles/submission-form.css';

// TSS vite/03 — proponer/editar una donadora externa (public-donor-submissions.md,
// Q-122). Modal role="dialog". FarmSelect (origin_farm_id*), RaceSelect
// (race_id*), Fields opcionales (name, number, birth, appearance,
// purchase_value, purchase_date), sex (default female). La edición solo llega
// aquí con propuestas `pending` (la lista deshabilita el resto).
//
// purchase_value es INPUT del cliente (económico provisional 0047): se declara,
// no se deriva — la regla "el front no calcula dinero" prohíbe derivar.
// NUNCA se envía register/verification_state/animal_id/client_id/status.

const SEX_OPTIONS = [
    { value: 'female', label: 'Hembra' },
    { value: 'male', label: 'Macho' },
];

export default function SubmissionForm({ submission, onClose, onSaved }) {
    const editing = !!submission;
    const qc = useQueryClient();
    const create = useCreateSubmission();
    const update = useUpdateSubmission(submission?.id);
    const busy = create.isPending || update.isPending;

    // Deduped con el FarmSelect (misma queryKey) — bloquea el submit sin fincas.
    const { data: farmsData } = useClientFarms({ limit: 500, offset: 0 });
    const hasFarms = (envelopeData(farmsData).items ?? []).length > 0;

    const [farmId, setFarmId] = useState(submission?.origin_farm?.id ?? null);
    const [raceId, setRaceId] = useState(submission?.race?.id ?? null);
    const [name, setName] = useState(submission?.name ?? '');
    const [number, setNumber] = useState(submission?.number ?? '');
    const [sex, setSex] = useState(submission?.sex ?? 'female');
    const [birth, setBirth] = useState(submission?.birth ?? '');
    const [appearance, setAppearance] = useState(submission?.appearance ?? '');
    const [purchaseValue, setPurchaseValue] = useState(
        submission?.purchase_value != null ? String(submission.purchase_value) : '',
    );
    const [purchaseDate, setPurchaseDate] = useState(submission?.purchase_date ?? '');
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const localError = useMemo(() => {
        if (!farmId) return { origin_farm_id: 'Selecciona la finca de origen.' };
        if (!raceId) return { race_id: 'Selecciona la raza.' };
        if (name.trim().length > SUBMISSION_NAME_MAX) return { name: `Máximo ${SUBMISSION_NAME_MAX} caracteres.` };
        if (number.trim().length > SUBMISSION_NUMBER_MAX) return { number: `Máximo ${SUBMISSION_NUMBER_MAX} caracteres.` };
        if (appearance.trim().length > SUBMISSION_APPEARANCE_MAX) {
            return { appearance: `Máximo ${SUBMISSION_APPEARANCE_MAX} caracteres.` };
        }
        if (purchaseValue !== '' && !(Number(purchaseValue) >= 0)) {
            return { purchase_value: 'El valor debe ser un número ≥ 0 (o vacío).' };
        }
        return null;
    }, [farmId, raceId, name, number, appearance, purchaseValue]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (localError || busy || !hasFarms) return;

        // Subconjunto documentado del contrato; vacíos → null.
        const payload = {
            origin_farm_id: farmId,
            race_id: raceId,
            name: name.trim() || null,
            number: number.trim() || null,
            sex,
            birth: birth || null,
            appearance: appearance.trim() || null,
            purchase_value: purchaseValue !== '' ? Number(purchaseValue) : null,
            purchase_date: purchaseDate || null,
        };

        setFieldErrors({});
        const mutation = editing ? update : create;
        mutation.mutate(payload, {
            onSuccess: () => {
                toast.success(editing ? 'Propuesta actualizada' : 'Donadora propuesta — quedará en revisión');
                onSaved?.();
            },
            onError: (err) => {
                if (err?.status === 404) {
                    toast.error('Propuesta no encontrada');
                    qc.invalidateQueries({ queryKey: submissionKeys.all });
                    onClose?.();
                    return;
                }
                if (err?.status === 409) {
                    // Defensivo: la UI ya deshabilita editar fuera de `pending`.
                    toast.error('Esta propuesta ya fue revisada y no puede modificarse');
                    qc.invalidateQueries({ queryKey: submissionKeys.all });
                    onClose?.();
                    return;
                }
                const f = (err?.fields && typeof err.fields === 'object') ? { ...err.fields } : {};
                if (err?.status === 422 && !f.origin_farm_id) {
                    f.origin_farm_id = 'Esa finca no es válida. Elige una de tus fincas.';
                }
                setFieldErrors(f);
                if (err?.status !== 403 && Object.keys(f).length === 0) {
                    toast.error(err?.message || 'No se pudo guardar la propuesta');
                }
            },
        });
    };

    const farmError = (!hasFarms ? null : localError?.origin_farm_id) || fieldErrors.origin_farm_id;
    const raceError = localError?.race_id || fieldErrors.race_id;
    const nameError = localError?.name || fieldErrors.name;
    const numberError = localError?.number || fieldErrors.number;
    const appearanceError = localError?.appearance || fieldErrors.appearance;
    const valueError = localError?.purchase_value || fieldErrors.purchase_value;
    const birthError = fieldErrors.birth;
    const dateError = fieldErrors.purchase_date;
    const sexError = fieldErrors.sex;

    return (
        <div
            className="submission-form"
            role="dialog"
            aria-modal="true"
            aria-label={editing ? 'Editar propuesta' : 'Proponer donadora'}
        >
            <div className="submission-form__overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
            <form className="submission-form__card" onSubmit={handleSubmit} noValidate>
                <header className="submission-form__head">
                    <div>
                        <h2 className="submission-form__title">
                            {editing ? 'Editar propuesta' : 'Proponer donadora'}
                        </h2>
                        <p className="submission-form__sub">
                            El equipo de Manantiales la revisará antes de registrarla.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="submission-form__close"
                        onClick={onClose}
                        disabled={busy}
                        aria-label="Cerrar"
                    >
                        <IconClose />
                    </button>
                </header>

                <div className="submission-form__body">
                    <Field label="Finca de origen" name="origin_farm_id" required error={farmError}>
                        <FarmSelect value={farmId} onChange={setFarmId} error={!!farmError} disabled={busy} />
                    </Field>
                    <Field label="Raza" name="race_id" required error={raceError}>
                        <RaceSelect value={raceId} onChange={setRaceId} error={!!raceError} disabled={busy} />
                    </Field>

                    <div className="submission-form__row">
                        <Field
                            label="Nombre"
                            name="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={nameError}
                            maxLength={SUBMISSION_NAME_MAX}
                            disabled={busy}
                            placeholder="Opcional"
                        />
                        <Field
                            label="Número (arete)"
                            name="number"
                            value={number}
                            onChange={(e) => setNumber(e.target.value)}
                            error={numberError}
                            maxLength={SUBMISSION_NUMBER_MAX}
                            disabled={busy}
                            placeholder="Opcional"
                        />
                    </div>

                    <div className="submission-form__row">
                        <Field label="Sexo" name="sex" error={sexError}>
                            <div className="submission-form__sex" role="radiogroup" aria-label="Sexo">
                                {SEX_OPTIONS.map((opt) => (
                                    <label key={opt.value} className="submission-form__sex-option">
                                        <input
                                            type="radio"
                                            name="sex"
                                            value={opt.value}
                                            checked={sex === opt.value}
                                            onChange={() => setSex(opt.value)}
                                            disabled={busy}
                                        />
                                        <span>{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <Field
                            label="Fecha de nacimiento"
                            name="birth"
                            type="date"
                            value={birth}
                            onChange={(e) => setBirth(e.target.value)}
                            error={birthError}
                            disabled={busy}
                        />
                    </div>

                    <Field
                        label="Apariencia"
                        name="appearance"
                        value={appearance}
                        onChange={(e) => setAppearance(e.target.value)}
                        error={appearanceError}
                        maxLength={SUBMISSION_APPEARANCE_MAX}
                        disabled={busy}
                        placeholder="Opcional — color, señas, características"
                    />

                    <div className="submission-form__row">
                        <Field
                            label="Valor de compra (COP)"
                            name="purchase_value"
                            type="number"
                            value={purchaseValue}
                            onChange={(e) => setPurchaseValue(e.target.value)}
                            error={valueError}
                            disabled={busy}
                            placeholder="Opcional"
                        />
                        <Field
                            label="Fecha de compra"
                            name="purchase_date"
                            type="date"
                            value={purchaseDate}
                            onChange={(e) => setPurchaseDate(e.target.value)}
                            error={dateError}
                            disabled={busy}
                        />
                    </div>
                </div>

                <footer className="submission-form__foot">
                    <button
                        type="button"
                        className="submission-form__btn submission-form__btn--ghost"
                        onClick={onClose}
                        disabled={busy}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="submission-form__btn submission-form__btn--primary"
                        disabled={busy || !!localError || !hasFarms}
                    >
                        {busy ? 'Enviando…' : editing ? 'Guardar cambios' : 'Proponer donadora'}
                    </button>
                </footer>
            </form>
        </div>
    );
}
